import React, { useState, useEffect } from 'react';
import { Button, Col, Form, ListGroup, Row } from 'react-bootstrap';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import {
  CheckOperator,
  MemoryCheckValueType,
  MemoryDTO,
  MemoryLinkValue,
  MemorySingleLink,
  MemoryType,
  MemoryValue,
  StepCheckSwitch,
} from '../../../../Book/MemoryBank/memoryBankTypes';
import { Branch } from 'dorian-shared/types/branch/Branch';
import { MemoryBankCheckValue } from '../MemorySlotCompareForm';
import { ConditionListItemForm } from './ConditionListItemForm';
import { CustomSelect } from '../../../CustomSelect';
import { noop } from 'helpers/noop';
import './IntValueOrVariableSelect.scss';

export interface ConditionListFormProps {
  inputNamePrefix: string,
  operatorInputValue: string,
  checkInputValue: MemoryLinkValue | MemoryValue,
  memoryBankSlots?: MemoryDTO[],
  stepIndex: number,
  onChangeValue: (
    values: MemoryBankCheckValue[],
    stepIndex: number,
  ) => void,
  isDisabled?: boolean,
  switchData: StepCheckSwitch[],
  branches: Branch[],
  currentBranchId: number,
  handleChangeStepSwitch: (gotoBranchId: number, stepIndex: number, switchIndex: number) => void,
  handleChangeCondition: (operator: string, intValue: string, stepIndex: number, switchIndex: number) => void,
  handleChangeVariableCondition: (operator: string, varValue: number, stepIndex: number, switchIndex: number) => void,
  onAddCondition: (stepIndex: number) => void,
  onRemoveCondition: (stepIndex: number, switchIndex: number) => void,
  onReorderConditions?: (stepIndex: number, oldIndex: number, newIndex: number) => void,
}

const MAX_CONDITIONS = 11;

// Sortable wrapper for individual condition row
const SortableConditionItem = SortableElement(({ 
  data, 
  conditionIndex, 
  totalConditions,
  variableName,
  stepIndex,
  currentBranchId,
  handleChangeStepSwitch,
  handleChangeCondition,
  handleChangeVariableCondition,
  handleRemoveCondition,
  branches,
  variables,
  leftOperandVariableId,
  isDisabled,
}: any) => {
  const isLastRow = conditionIndex === totalConditions - 1;
  return (
    <ListGroup.Item style={{ marginBottom: 0 }}>
      <ConditionListItemForm
        stepIndex={stepIndex}
        switchIndex={conditionIndex}
        gotoBranchId={data.gotoBranchId}
        currentBranchId={currentBranchId}
        handleChangeStepSwitch={handleChangeStepSwitch}
        handleChangeCondition={handleChangeCondition}
        handleChangeVariableCondition={handleChangeVariableCondition}
        handleRemoveCondition={handleRemoveCondition}
        branches={branches}
        variables={variables}
        leftOperandVariableId={leftOperandVariableId}
        isDisabled={isDisabled}
        conditionNumber={conditionIndex + 1}
        isLastRow={isLastRow}
        totalConditions={totalConditions}
        variableName={variableName}
        operator={data.operator}
        intValue={data.intValue}
        varValue={data.varValue}
      />
    </ListGroup.Item>
  );
});

// Sortable wrapper for the list of conditions (no variant="flush" to show borders)
const SortableConditionList = SortableContainer(({ conditions, variableName, ...props }: any) => (
  <ListGroup>
    {conditions.map((data: any, index: number) => {
      const isLastRow = index === conditions.length - 1;
      return (
        <SortableConditionItem
          key={index}
          index={index}
          disabled={isLastRow || props.isDisabled}
          data={data}
          conditionIndex={index}
          totalConditions={conditions.length}
          variableName={variableName}
          {...props}
        />
      );
    })}
  </ListGroup>
));

export function ConditionListForm(props: ConditionListFormProps) {
  const {
    inputNamePrefix,
    operatorInputValue,
    checkInputValue,
    memoryBankSlots,
    stepIndex,
    onChangeValue,
    isDisabled,
    switchData,
    branches,
    currentBranchId,
    handleChangeStepSwitch,
    handleChangeCondition,
    handleChangeVariableCondition,
    onAddCondition,
    onRemoveCondition,
    onReorderConditions,
  } = props;

  const memories = memoryBankSlots?.filter(
    (memory) => memory.type === MemoryType.Number,
  );

  if (!memories) {
    return null;
  }

  const handleAddCondition = () => {
    if (switchData && switchData.length < MAX_CONDITIONS) {
      onAddCondition(stepIndex);
    }
  };

  const handleSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    if (onReorderConditions) {
      const lastIndex = conditions.length - 1;
      
      // Don't allow dragging FROM the last position (the "Otherwise" row)
      if (oldIndex === lastIndex) {
        return;
      }
      
      // If trying to drag TO the last position, place it second-to-last instead
      let adjustedNewIndex = newIndex;
      if (newIndex === lastIndex) {
        adjustedNewIndex = lastIndex - 1;
      }
      
      onReorderConditions(stepIndex, oldIndex, adjustedNewIndex);
    }
  };

  // For ConditionList, checkInputValue should be just the variableId string/number
  // Not a MemoryLinkValue object like Lowest/Highest
  let selectedVariableId = '';
  if (checkInputValue && typeof checkInputValue === 'object' && 'variableId' in checkInputValue) {
    // Handle MemoryLinkValue if coming from conversion
    const link = checkInputValue as MemorySingleLink;
    selectedVariableId = Array.isArray(link.variableId) ? link.variableId[0] : link.variableId;
  } else if (typeof checkInputValue === 'string' || typeof checkInputValue === 'number') {
    selectedVariableId = String(checkInputValue);
  }

  const selectedMemory = memories.find((m) => m.id.toString() === selectedVariableId);
  const selectedMemoryName = selectedMemory?.name || 'variable';

  // Ensure switchData is an array (handle undefined case)
  const conditions = switchData || [];

  const handleVariableSelect = (selectedId: number | string | null) => {
    // For ConditionList, just set variableId, NOT the MemoryLinkValue structure
    onChangeValue([
      {
        name: 'variableId',
        value: Number(selectedId),
      }
    ], stepIndex);
  };

  return (
    <Col>
      <Row xs={1} sm={2} lg={3}>
        <Form.Group as={Row}>
          <Col className="mx-2">
            <Form.Control
              disabled={isDisabled}
              size="sm"
              as="select"
              name={`${inputNamePrefix}[operator]`}
              value={operatorInputValue}
              onChange={(event) => onChangeValue([{
                name: 'operator',
                value: event.target.value,
              }], stepIndex)}
            >
              {Object.entries(CheckOperator).map(([operatorKey, operatorValue]) => {
                if (!operatorValue.isMultiSelect && !operatorValue.isConditionList) {
                  return null;
                }
                return (
                  <option key={`option${operatorKey}`} value={operatorKey}>
                    {operatorValue.title}
                  </option>
                );
              })}
            </Form.Control>
          </Col>
        </Form.Group>
        <Form.Group as={Row}>
          <Col md="auto" className="mx-1">
            <Form.Label column>
              of
            </Form.Label>
          </Col>
          <Col xl={6} lg={8} md={7}>
            <div className="conditionlist-variable-select">
              <CustomSelect
                value={selectedVariableId || ''}
                disabled={isDisabled}
                name={`${inputNamePrefix}[variableId]`}
                formControl="sm"
                args={[
                  { id: '', title: 'Select a variable' },
                  ...memories.map((memory) => ({
                    id: memory.id,
                    title: memory.name,
                  })),
                ]}
                onFocus={noop}
                onChange={handleVariableSelect}
              />
            </div>
          </Col>
        </Form.Group>
      </Row>

      {selectedMemory && conditions.length > 0 && (
        <div className="conditionlist-conditions-section">
          <div className="mt-3 mb-2">
            <Form.Text className="text-muted">
              Conditions are evaluated in order. The first condition that matches will execute.
            </Form.Text>
          </div>

          <div className="mt-2">
            {/* @ts-ignore - SortableContainer accepts custom props */}
            <SortableConditionList
              // @ts-ignore
              conditions={conditions}
              variableName={selectedMemoryName}
              distance={2}
              onSortEnd={handleSortEnd}
              helperClass="sort-item"
              stepIndex={stepIndex}
              currentBranchId={currentBranchId}
              handleChangeStepSwitch={handleChangeStepSwitch}
              handleChangeCondition={handleChangeCondition}
              handleChangeVariableCondition={handleChangeVariableCondition}
              handleRemoveCondition={onRemoveCondition}
              branches={branches}
              variables={memories}
              leftOperandVariableId={selectedMemory?.id}
              isDisabled={isDisabled}
            />
          </div>

          {conditions.length < MAX_CONDITIONS && (
            <div className="mt-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAddCondition}
                disabled={isDisabled}
              >
                + Add Condition
              </Button>
              <Form.Text className="text-muted ml-2">
                {conditions.length} / {MAX_CONDITIONS} conditions
              </Form.Text>
            </div>
          )}

          {/* Warning Section */}
          {(() => {
            const hasIncomplete = conditions.some((condition, index) => {
              // Skip the last row (Otherwise row)
              if (index === conditions.length - 1) {
                return !condition.gotoBranchId || condition.gotoBranchId === 0;
              }
              // Check non-last rows for empty intValue/varValue or gotoBranchId
              const hasNoValue = (!condition.intValue || condition.intValue === '') && 
                                 (!condition.varValue || condition.varValue === 0);
              return hasNoValue || 
                     !condition.gotoBranchId || 
                     condition.gotoBranchId === 0;
            });

            if (hasIncomplete) {
              return (
                <div className="mt-3">
                  <div style={{ 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7', 
                    borderRadius: '4px', 
                    padding: '10px',
                    color: '#856404'
                  }}>
                    <strong>⚠️ Warning:</strong> Some conditions are incomplete.
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Error Section */}
          {(() => {
            const errors: string[] = [];
            
            // Check for duplicate operator+value pairs (ignoring empty values and last row)
            const conditionsWithValues: Array<{ operator: string, value: string, displayValue: string, index: number }> = [];
            conditions.forEach((condition, index) => {
              // Skip last row (Otherwise)
              if (index === conditions.length - 1) {
                return;
              }
              
              // Check if this condition has a value (either intValue or varValue)
              const hasIntValue = condition.intValue && condition.intValue !== '';
              const hasVarValue = condition.varValue && condition.varValue !== 0;
              
              if (!hasIntValue && !hasVarValue) {
                return; // Skip empty conditions
              }
              
              const currentOperator = condition.operator || 'equal';
              let valueKey: string;
              let displayValue: string;
              
              if (hasVarValue) {
                const variable = memories.find(m => m.id === condition.varValue);
                valueKey = `var_${condition.varValue}`;
                displayValue = variable?.name || 'Unknown Variable';
              } else {
                valueKey = `int_${condition.intValue}`;
                displayValue = String(condition.intValue);
              }
              
              conditionsWithValues.push({
                operator: currentOperator,
                value: valueKey,
                displayValue: displayValue,
                index: index + 1,
              });
            });

            // Find duplicates
            const seen = new Map<string, Array<{ index: number, displayValue: string, operator: string }>>();
            conditionsWithValues.forEach(({ operator, value, displayValue, index }) => {
              const key = `${operator}${value}`;
              if (!seen.has(key)) {
                seen.set(key, []);
              }
              seen.get(key)?.push({ index, displayValue, operator });
            });

            seen.forEach((duplicates, key) => {
              if (duplicates.length > 1) {
                const operatorKey = duplicates[0].operator;
                const operatorDisplay = CheckOperator[operatorKey as keyof typeof CheckOperator]?.title || operatorKey;
                const displayValue = duplicates[0].displayValue;
                const indices = duplicates.map(d => d.index);
                errors.push(`Duplicate condition: "${operatorDisplay} ${displayValue}" appears in rows ${indices.join(', ')}`);
              }
            });

            if (errors.length > 0) {
              return (
                <div className="mt-3" style={{ marginLeft: '-15px', marginRight: '-15px', paddingLeft: '15px', paddingRight: '15px' }}>
                  <div style={{ 
                    backgroundColor: '#f8d7da', 
                    border: '1px solid #f5c6cb', 
                    borderRadius: '4px', 
                    padding: '10px',
                    color: '#721c24'
                  }}>
                    <strong>❌ Potential issues:</strong>
                    <ul className="mb-0 mt-2" style={{ paddingLeft: '20px' }}>
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </Col>
  );
}

